import { MultiFactorAuthenticationCodeError } from '@pba/client-auth';
import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Flex, Form, Icon, Input, Item, Row, Text, Title, message } from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import { useSetIsAuthenticated } from 'features/auth';
import { useLoginToNodeMutation } from 'features/users';

type P<T> = {
  onNavigateToPrimaryLogin: () => Promise<void>;
  onSuccessLogin: () => Promise<void>;
  state?: T;
};

const TIME_TO_CAN_RESET_PASSWORD = 60;

type OneTimeCodeCheckFormValues = {
  password: string;
};

const OneTimeCodeCheck = <T extends { tokenToGetCode?: string; tokenToLogin?: string }>({
  onNavigateToPrimaryLogin,
  onSuccessLogin,
  state,
}: P<T>) => {
  const { isPending, mutate: loginToNode } = useLoginToNodeMutation();
  const [secondsUntilResend, setSecondsUntilResend] = useState(TIME_TO_CAN_RESET_PASSWORD);
  const setIsAuthenticated = useSetIsAuthenticated();
  const [codeToLogin, setCodeToLogin] = useState(state?.tokenToLogin);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsUntilResend((current) => Math.max(0, current - 1));
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const resendCode = useCallback(() => {
    loginToNode(
      { strategy: 'code', token: state?.tokenToGetCode ?? '' },
      {
        onError(getChallengeError) {
          if (getChallengeError instanceof MultiFactorAuthenticationCodeError) {
            setCodeToLogin(getChallengeError.axiosError.response.data.data.token);
          } else {
            handleAxiosError(getChallengeError);
          }
        },
      },
    );

    setSecondsUntilResend(TIME_TO_CAN_RESET_PASSWORD);
  }, [loginToNode, state?.tokenToGetCode]);

  const onSubmit = useCallback(
    (data: OneTimeCodeCheckFormValues) => {
      loginToNode(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We expect that token will be in here
        { password: data.password, strategy: 'code', token: codeToLogin! },
        {
          // TODO: tuto mozu prist rozne errory to sa musi ohandlovat poriadne
          onError() {
            message.error('Invalid password');
          },
          async onSuccess() {
            setIsAuthenticated(true);
            await onSuccessLogin();
          },
        },
      );
    },
    [codeToLogin, loginToNode, onSuccessLogin, setIsAuthenticated],
  );

  return (
    <Flex gap={32} vertical>
      <Title level={1}>One-time password sent</Title>
      <Text type="secondary">
        We&apos;ve sent you an email with a one-time password. Please check your inbox and enter the password below. The
        password is valid for 5 minutes from the time it was requested.
      </Text>
      <Form<OneTimeCodeCheckFormValues> onFinish={onSubmit}>
        <Item
          label="Password from e-mail"
          name="password"
          rules={[
            {
              required: true,
              whitespace: true,
            },
          ]}
        >
          <Input />
        </Item>
        <Row gutter={[0, 64]}>
          <Col span={24}>
            {secondsUntilResend > 0 ? (
              <div>
                <Text>You can request new password in </Text>
                <Text strong>{secondsUntilResend}s</Text>
              </div>
            ) : (
              <Button onClick={resendCode} type="link-primary">
                Request new password
              </Button>
            )}
          </Col>
          <Col span={24}>
            <Row gutter={[0, 'ms']}>
              <Col span={24}>
                <Button block htmlType="submit" loading={isPending} type="primary">
                  Log in
                </Button>
              </Col>
              <Col span={24}>
                <Button
                  block
                  icon={<Icon icon="arrow-left-line" />}
                  onClick={async () => {
                    await onNavigateToPrimaryLogin();
                  }}
                >
                  All log in options
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Flex>
  );
};

export default OneTimeCodeCheck;
